import {
    AnvilSelect,
    AnvilSelectOptionsProps,
    Button,
    ButtonGroup,
    Icon,
    Stack,
    useScreenSize,
} from '@servicetitan/design-system';
import { FC } from 'react';
import * as Styles from './zoom.module.less';
import classNames from 'classnames';

const qaTag = (name: string) => `pdf-viewer-zoom-${name}`;

const zoomValues = [
    0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5,
] as const;

export interface ZoomProps {
    value: number;
    onChange: (value: number) => void;
    onFitToWidth?: () => void;
    onFitToHeight?: () => void;
}

export const Zoom: FC<ZoomProps> = props => {
    const { value, onChange } = props;

    const screenSize = useScreenSize();

    const isMobile = screenSize.smallerThan.mobile;

    const zoomInDisabled = value >= zoomValues[zoomValues.length - 1];
    const zoomOutDisabled = value <= zoomValues[0];

    const options = zoomValues.map(
        v =>
            ({
                text: `${(v * 100).toFixed(0)}%`,
                value: v,
            }) as AnvilSelectOptionsProps
    );

    if (!zoomValues.includes(value as never)) {
        options.push({
            text: `${(value * 100).toFixed(0)}%`,
            value,
        } as AnvilSelectOptionsProps);
    }

    const handleChange = (anvilProps: AnvilSelectOptionsProps | AnvilSelectOptionsProps[]) => {
        onChange((anvilProps as AnvilSelectOptionsProps).value);
    };

    const zoomOut = () => {
        const index = zoomValues.indexOf(value as never);
        if (index > 0) {
            onChange(zoomValues[index - 1]);
        } else if (index === -1) {
            // find closest value in zoomValues
            let curValue = zoomValues[0] as number;
            for (const item of zoomValues) {
                if (item < value) {
                    curValue = item;
                } else {
                    break;
                }
            }
            onChange(curValue);
        }
    };

    const zoomIn = () => {
        const index = zoomValues.indexOf(value as never);
        if (index !== -1 && index < zoomValues.length - 1) {
            onChange(zoomValues[index + 1]);
        } else if (index === -1) {
            // find closest value in zoomValues
            let curValue = zoomValues[0] as number;
            for (const item of zoomValues) {
                if (item > value) {
                    curValue = item;
                    break;
                }
            }
            onChange(curValue);
        }
    };

    const selectZoomTrigger = {
        clearable: false,
        className: classNames(Styles.zoomInput, `qa-${qaTag('select-trigger')}`),
        size: isMobile ? 'small' : 'medium',
    };

    return (
        <Stack direction="row" alignItems="center" spacing={isMobile ? 1 : 4}>
            <ButtonGroup attached>
                <Button
                    outline
                    fill="subtle"
                    disabled={zoomOutDisabled}
                    icon={<Icon name="zoom_out" />}
                    data-qa={qaTag('out')}
                    onClick={zoomOut}
                    className="border"
                    size={isMobile ? 'small' : 'medium'}
                />
                <Button
                    outline
                    fill="subtle"
                    disabled={zoomInDisabled}
                    icon={<Icon name="zoom_in" />}
                    onClick={zoomIn}
                    data-qa={qaTag('in')}
                    className="border"
                    size={isMobile ? 'small' : 'medium'}
                />
            </ButtonGroup>
            <AnvilSelect
                options={options}
                value={value}
                trigger={selectZoomTrigger}
                onChange={handleChange}
                scrollHeight="350px"
            />
        </Stack>
    );
};
